import React, { useEffect, useRef, useState } from 'react';
import './MobileStartVisit.css';
import { exportSOAPNoteToPDF } from '../utils/soapExport';

function MobileStartVisit({
  patients,
  patientSearchQuery,
  setPatientSearchQuery,
  showPatientDropdown,
  setShowPatientDropdown,
  selectedPatient,
  handleSelectPatient,
  setShowPatientModal,
  onClearSelectedPatient,
  appointmentDate,
  setAppointmentDate,
  appointmentDateError,
  setAppointmentDateError,
  onContinue,
  onStartRecording,
  isRecording,
  recordingDuration,
  formatDuration,
  soapNote,
  patientName,
  isEditing,
  editedSoapNote,
  handleEditField,
  handleEditSOAP,
  handleCancelEdit,
  handleSaveEdit,
  saveToHistory,
  isSavingHistory,
  isSaved,
  hasUnsavedChanges,
  copied,
  handleCopySOAP,
  onBackToStart,
  editingCodeIndex,
  editedCodeValue,
  setEditedCodeValue,
  editedCodeDescription,
  setEditedCodeDescription,
  editedCodeTiming,
  setEditedCodeTiming,
  startEditingCode,
  saveEditedCode,
  cancelEditingCode,
  addNewCode,
  deleteCode,
  currentUser,
  selectedFile,
  handleFileUpload,
  fileInputRef,
  handleGenerateSOAP,
  isGenerating,
}) {
  const [step, setStep] = useState('start');
  const [hasStoppedRecording, setHasStoppedRecording] = useState(false);
  const mobileFileInputRef = useRef(null);
  const safeQuery = patientSearchQuery || '';
  const canContinue = Boolean(appointmentDate) && !appointmentDateError;
  const hasSoapNote = Boolean(soapNote && soapNote.soap_generated && soapNote.soap_note);
  const clinicalSupport = soapNote?.clinical_support;
  const hasClinicalSupport = Boolean(soapNote?.ai_support_generated && clinicalSupport);
  const canSaveHistory = !isSavingHistory && (!isSaved || hasUnsavedChanges);
  const noteContent = isEditing && editedSoapNote ? editedSoapNote : soapNote?.soap_note || {};
  const formattedDuration = formatDuration
    ? formatDuration(recordingDuration)
    : (() => {
        const mins = Math.floor((recordingDuration || 0) / 60);
        const secs = (recordingDuration || 0) % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
      })();
  const filteredPatients = (patients || []).filter(patient => {
    const query = safeQuery.toLowerCase();
    return (
      patient.full_name.toLowerCase().includes(query) ||
      (patient.date_of_birth && patient.date_of_birth.includes(query)) ||
      (patient.email && patient.email.toLowerCase().includes(query)) ||
      (patient.phone && patient.phone.includes(query))
    );
  });

  useEffect(() => {
    if (step !== 'appointment' || appointmentDate) {
      return;
    }

    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    setAppointmentDate(`${year}-${month}-${day}`);
    setAppointmentDateError('');
  }, [step, appointmentDate, setAppointmentDate, setAppointmentDateError]);

  useEffect(() => {
    if (hasSoapNote) {
      setStep('result');
      return;
    }
    if (step === 'result') {
      setStep('start');
    }
  }, [hasSoapNote, step]);

  useEffect(() => {
    if (step !== 'record') {
      setHasStoppedRecording(false);
    }
  }, [step]);

  useEffect(() => {
    if (selectedFile && !hasSoapNote && step !== 'record') {
      setStep('record');
    }
  }, [selectedFile, hasSoapNote, step]);

  const renderNoteSection = (title, content, fieldKey) => (
    <section className="mobile-start-visit-note-section" key={title}>
      <h3>{title}</h3>
      {isEditing && handleEditField && fieldKey ? (
        <textarea
          className="mobile-start-visit-note-textarea"
          value={content || ''}
          onChange={(event) => handleEditField(fieldKey, event.target.value)}
        />
      ) : (
        <div className="mobile-start-visit-note-text">
          {formatNoteText(content)}
        </div>
      )}
    </section>
  );

  const renderSupportList = (title, items) => {
    if (!items || items.length === 0) return null;
    return (
      <section className="mobile-start-visit-note-section" key={title}>
        <h3>{title}</h3>
        <ul className="mobile-start-visit-note-list">
          {items.map((item, index) => (
            <li key={`${title}-${index}`}>{formatNoteText(item)}</li>
          ))}
        </ul>
      </section>
    );
  };

  const renderBillingCodes = () => {
    if (!clinicalSupport?.billing_codes || clinicalSupport.billing_codes.length === 0) {
      if (!addNewCode) return null;
      return (
        <section className="mobile-start-visit-note-section">
          <h3>CDA Billing Codes</h3>
          <p className="mobile-start-visit-note-disclaimer">
            AI-generated suggestions - please verify before billing.
          </p>
          <button className="mobile-billing-add-btn" type="button" onClick={addNewCode}>
            + Add Code
          </button>
        </section>
      );
    }

    const timingOptions = ['Today', 'In 1 week', 'In 2 weeks', 'In 2-4 weeks', 'In 1 month', 'In 3 months', 'In 6 months', 'Next visit'];

    return (
      <section className="mobile-start-visit-note-section">
        <h3>CDA Billing Codes</h3>
        <p className="mobile-start-visit-note-disclaimer">
          AI-generated suggestions - please verify before billing.
        </p>
        <div className="mobile-billing-codes-list">
          {clinicalSupport.billing_codes.map((code, index) => {
            if (typeof code === 'string') {
              return (
                <div key={`code-${index}`} className="mobile-billing-code-item">
                  <span>{formatNoteText(code)}</span>
                </div>
              );
            }
            const isEditingThis = editingCodeIndex === index;
            const codeValue = code.code || code.cda_code || '';
            const description = code.description || code.procedure || '';
            const timing = code.timing || '';

            return (
              <div key={`code-${index}`} className={`mobile-billing-code-item${isEditingThis ? ' is-editing' : ''}`}>
                {isEditingThis ? (
                  <div className="mobile-billing-code-edit">
                    <input
                      className="mobile-billing-input mobile-billing-input-code"
                      type="text"
                      placeholder="Code (e.g. 11101)"
                      value={editedCodeValue}
                      onChange={(e) => setEditedCodeValue(e.target.value)}
                    />
                    <input
                      className="mobile-billing-input"
                      type="text"
                      placeholder="Procedure description"
                      value={editedCodeDescription}
                      onChange={(e) => setEditedCodeDescription(e.target.value)}
                    />
                    <select
                      className="mobile-billing-select"
                      value={editedCodeTiming}
                      onChange={(e) => setEditedCodeTiming(e.target.value)}
                    >
                      {timingOptions.map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                    <div className="mobile-billing-code-actions">
                      <button
                        className="mobile-billing-btn mobile-billing-btn-save"
                        type="button"
                        onClick={() => saveEditedCode(index)}
                      >
                        Save
                      </button>
                      <button
                        className="mobile-billing-btn mobile-billing-btn-cancel"
                        type="button"
                        onClick={cancelEditingCode}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="mobile-billing-code-view">
                    <div className="mobile-billing-code-info">
                      <strong className="mobile-billing-code-number">{codeValue}</strong>
                      {description && <span className="mobile-billing-code-desc">{formatNoteText(description)}</span>}
                      {timing && <span className="mobile-billing-code-timing">{timing}</span>}
                    </div>
                    <div className="mobile-billing-code-actions">
                      <button
                        className="mobile-billing-btn mobile-billing-btn-edit"
                        type="button"
                        onClick={() => startEditingCode(index, code)}
                        aria-label="Edit code"
                      >
                        Edit
                      </button>
                      <button
                        className="mobile-billing-btn mobile-billing-btn-delete"
                        type="button"
                        onClick={() => deleteCode(index)}
                        aria-label="Delete code"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {addNewCode && (
          <button className="mobile-billing-add-btn" type="button" onClick={addNewCode}>
            + Add Code
          </button>
        )}
      </section>
    );
  };

  const formatNoteText = (value) => {
    if (!value) return 'N/A';
    return value
      .replace(/\*\*\s*(.*?)\s*\*\*/g, '$1')
      .replace(/\*\s*(.*?)\s*\*/g, '$1')
      .trim();
  };

  return (
    <div className={`mobile-start-visit-shell is-step-${step}`}>
      <div className="mobile-start-visit-panel mobile-start-visit-panel-start">
        <button
          className="mobile-start-visit-button"
          type="button"
          onClick={() => setStep('patient')}
        >
          Start a Visit
        </button>
      </div>
      <div className="mobile-start-visit-panel mobile-start-visit-panel-patient">
        <div className="mobile-start-visit-card">
          <button
            className="mobile-start-visit-back"
            type="button"
            onClick={() => setStep('start')}
          >
            Go back
          </button>
          <h2 className="mobile-start-visit-title">Search or add a patient</h2>
          <div className="patient-search-container mobile-start-visit-search">
            <input
              className="mobile-start-visit-input"
              type="text"
              placeholder="Search..."
              value={safeQuery}
              onChange={(event) => {
                setPatientSearchQuery(event.target.value);
                setShowPatientDropdown(true);
              }}
              onFocus={() => setShowPatientDropdown(true)}
              onBlur={() => setTimeout(() => setShowPatientDropdown(false), 200)}
            />
            {selectedPatient && (
              <button
                className="input-clear"
                type="button"
                onClick={() => {
                  if (onClearSelectedPatient) {
                    onClearSelectedPatient();
                  }
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            )}
            {showPatientDropdown && (
              <div className="patient-dropdown">
                {filteredPatients.map(patient => (
                  <div
                    key={patient.id}
                    onClick={() => {
                      handleSelectPatient(patient);
                      setPatientSearchQuery(patient.full_name);
                      setShowPatientDropdown(false);
                      setStep('appointment');
                    }}
                    className={`patient-dropdown-item ${selectedPatient?.id === patient.id ? 'is-selected' : ''}`}
                  >
                    <div className="patient-name">{patient.full_name}</div>
                    <div className="patient-info">
                      {patient.date_of_birth && <span>DOB: {patient.date_of_birth}</span>}
                      {patient.email && <span>📧 {patient.email}</span>}
                    </div>
                  </div>
                ))}
                {filteredPatients.length === 0 && (
                  <div className="patient-empty">No patients found matching "{safeQuery}"</div>
                )}
              </div>
            )}
          </div>
          <button
            className="mobile-start-visit-submit"
            type="button"
            onClick={() => setShowPatientModal(true)}
          >
            Add a patient
          </button>
        </div>
      </div>
      <div className="mobile-start-visit-panel mobile-start-visit-panel-appointment">
        <div className="mobile-start-visit-card">
          <button
            className="mobile-start-visit-back"
            type="button"
            onClick={() => setStep('patient')}
          >
            Go back
          </button>
          <h2 className="mobile-start-visit-title">Select appointment date</h2>
          <input
            className="mobile-start-visit-input"
            type="date"
            value={appointmentDate}
            max={(() => {
              const today = new Date();
              const year = today.getFullYear();
              const month = String(today.getMonth() + 1).padStart(2, '0');
              const day = String(today.getDate()).padStart(2, '0');
              return `${year}-${month}-${day}`;
            })()}
            onChange={(event) => {
              const selectedDate = event.target.value;
              const today = new Date();
              const year = today.getFullYear();
              const month = String(today.getMonth() + 1).padStart(2, '0');
              const day = String(today.getDate()).padStart(2, '0');
              const todayString = `${year}-${month}-${day}`;

              if (selectedDate > todayString) {
                setAppointmentDateError('Appointment date cannot be in the future.');
                setAppointmentDate(selectedDate);
              } else {
                setAppointmentDateError('');
                setAppointmentDate(selectedDate);
              }
            }}
          />
          {appointmentDateError && (
            <div className="field-error">
              <span>{appointmentDateError}</span>
            </div>
          )}
          <button
            className="mobile-start-visit-continue"
            type="button"
            disabled={!canContinue}
            onClick={() => {
              if (!canContinue) return;
              if (onContinue) {
                onContinue();
              }
              setStep('record');
            }}
          >
            Continue
          </button>
        </div>
      </div>
      <div className="mobile-start-visit-panel mobile-start-visit-panel-record">
        <div className="mobile-start-visit-card mobile-start-visit-card-record">
          <button
            className="mobile-start-visit-back"
            type="button"
            onClick={() => setStep('appointment')}
          >
            Go back
          </button>
          {selectedFile ? (
            <>
              <h2 className="mobile-start-visit-title">Ready to generate</h2>
              <div className="mobile-record-file-ready">
                <svg viewBox="0 0 24 24" aria-hidden="true" className="mobile-record-file-icon">
                  <path d="M9 18V12L7 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M9 12L11 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M14 2H6C5.5 2 5 2.5 5 3V21C5 21.5 5.5 22 6 22H18C18.5 22 19 21.5 19 21V7L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M14 2V7H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <p className="mobile-record-file-name">{selectedFile.name}</p>
              </div>
              <button
                className="mobile-start-visit-continue"
                type="button"
                disabled={isGenerating}
                onClick={handleGenerateSOAP}
              >
                {isGenerating ? 'Generating…' : 'Generate Note'}
              </button>
              <label className="mobile-record-attach-link" htmlFor="mobile-file-upload">
                Choose a different file
                <input
                  id="mobile-file-upload"
                  type="file"
                  accept="audio/*"
                  onChange={handleFileUpload}
                  ref={mobileFileInputRef}
                  style={{ display: 'none' }}
                />
              </label>
            </>
          ) : (
            <>
              <h2 className="mobile-start-visit-title">Start recording</h2>
              {isRecording && (
                <div className="mobile-start-visit-timer">{formattedDuration}</div>
              )}
              {!hasStoppedRecording && (
                <button
                  className={`mobile-start-visit-record-button ${isRecording ? 'is-recording' : ''}`}
                  type="button"
                  aria-label="Start recording"
                  onClick={() => {
                    if (isRecording) {
                      setHasStoppedRecording(true);
                    }
                    if (onStartRecording) {
                      onStartRecording();
                    }
                  }}
                >
                  <svg viewBox="0 0 120 120" aria-hidden="true">
                    <circle cx="60" cy="60" r="42" fill="currentColor" opacity="0.12" />
                    <circle cx="60" cy="60" r="16" fill="currentColor" />
                  </svg>
                </button>
              )}
              {!isRecording && !hasStoppedRecording && (
                <label className="mobile-record-attach-btn" htmlFor="mobile-file-upload">
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M17 8L12 3L7 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M12 3V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Attach Audio File
                  <input
                    id="mobile-file-upload"
                    type="file"
                    accept="audio/*"
                    onChange={handleFileUpload}
                    ref={mobileFileInputRef}
                    style={{ display: 'none' }}
                  />
                </label>
              )}
            </>
          )}
        </div>
      </div>
      <div className="mobile-start-visit-panel mobile-start-visit-panel-result">
        <div className="mobile-start-visit-note">
          <div className="mobile-start-visit-note-header">
            <h2>SOAP Note</h2>
            {patientName && <p>Patient: {patientName}</p>}
            {appointmentDate && (
              <p>
                Date:{' '}
                {(() => {
                  const [year, month, day] = appointmentDate.split('-');
                  return new Date(year, month - 1, day).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  });
                })()}
              </p>
            )}
          </div>
          {hasSoapNote && (
            <>
              {renderNoteSection('Subjective', noteContent.subjective, 'subjective')}
              {renderNoteSection('Objective', noteContent.objective, 'objective')}
              {renderNoteSection('Assessment', noteContent.assessment, 'assessment')}
              {renderNoteSection('Plan', noteContent.plan, 'plan')}
              {hasClinicalSupport && (
                <section className="mobile-start-visit-note-section mobile-start-visit-support">
                  <h3>AI Clinical Support</h3>
                  {renderSupportList('Clinical Recommendations', clinicalSupport.clinical_recommendations)}
                  {renderSupportList('Patient Education', clinicalSupport.patient_education)}
                  {renderBillingCodes()}
                  {renderSupportList('Follow-up Plan', clinicalSupport.follow_up)}
                  {renderSupportList('Risk Assessment', clinicalSupport.risk_assessment)}
                </section>
              )}
              <div className="mobile-start-visit-actions">
                <button
                  className="mobile-start-visit-action mobile-start-visit-action-back"
                  type="button"
                  aria-label="Back to start"
                  onClick={() => {
                    if (onBackToStart) onBackToStart();
                  }}
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                <button
                  className={`mobile-start-visit-action ${isEditing ? 'is-active' : ''}`}
                  type="button"
                  aria-label={isEditing ? 'Cancel edit' : 'Edit SOAP note'}
                  onClick={() => {
                    if (isEditing && handleCancelEdit) {
                      handleCancelEdit();
                      return;
                    }
                    if (!isEditing && handleEditSOAP) {
                      handleEditSOAP();
                    }
                  }}
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    {isEditing ? (
                      <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    ) : (
                      <>
                        <path d="M11 4H4C3.5 4 3 4.5 3 5V20C3 20.5 3.5 21 4 21H19C19.5 21 20 20.5 20 20V13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M18.5 2.5C18.9 2.1 19.4 1.9 20 1.9C20.6 1.9 21.1 2.1 21.5 2.5C21.9 2.9 22.1 3.4 22.1 4C22.1 4.6 21.9 5.1 21.5 5.5L12 15L8 16L9 12L18.5 2.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </>
                    )}
                  </svg>
                </button>
                <button
                  className="mobile-start-visit-action"
                  type="button"
                  aria-label="Export PDF"
                  onClick={() => exportSOAPNoteToPDF({
                    patientName,
                    appointmentDate,
                    fileName: null,
                    soapNote,
                    dentistName: currentUser?.full_name,
                    licenseNumber: currentUser?.license_number,
                  })}
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M14 2H6C5.5 2 5 2.5 5 3V21C5 21.5 5.5 22 6 22H18C18.5 22 19 21.5 19 21V7L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M14 2V7H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M12 18V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M9 15L12 18L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                <button
                  className={`mobile-start-visit-action ${copied ? 'is-success' : ''}`}
                  type="button"
                  aria-label="Copy SOAP note"
                  onClick={handleCopySOAP}
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    {copied ? (
                      <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    ) : (
                      <>
                        <rect x="9" y="9" width="13" height="13" rx="2" stroke="currentColor" strokeWidth="2"/>
                        <path d="M5 15H4C3 15 2 14 2 13V4C2 3 3 2 4 2H13C14 2 15 3 15 4V5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </>
                    )}
                  </svg>
                </button>
                <button
                  className={`mobile-start-visit-action ${canSaveHistory ? '' : 'is-disabled'}`}
                  type="button"
                  aria-label="Save SOAP note"
                  disabled={!canSaveHistory}
                  onClick={() => {
                    if (!canSaveHistory) return;
                    if (isEditing && handleSaveEdit) {
                      handleSaveEdit();
                      return;
                    }
                    if (saveToHistory) {
                      saveToHistory();
                    }
                  }}
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M19 21H5C4.5 21 4 20.5 4 20V5C4 4.5 4.5 4 5 4H16L21 9V20C21 20.5 20.5 21 20 21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M17 21V13H7V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M7 4V9H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default MobileStartVisit;
